import React, { useState, useRef, useMemo, useEffect } from 'react';
import clsx from 'clsx';
import { SymbolType, CdnObjectType } from '@/types';
import { useUi, useAssetCdn } from '@/context/Ui';
import { useLessonPlayer } from '@/context/LessonPlayer';
import { AudioPlayer, CdnMedia, Celebrate, RoundButton } from '@/components';
import { WhitePanel } from './WhitePanel';
import { Toolbar } from './Toolbar';

interface LineProps {
  className?: string;
}

const Line = ({ className }: LineProps) => (
  <div className={clsx('w-36 h-3 mb-6 rounded bg-white', className)}>
    &nbsp;
  </div>
);

interface LinesProps {
  className?: string;
  onClick?: () => void;
  selected?: boolean;
  isCorrect?: boolean;
}

const Lines = ({
  className,
  onClick,
  selected = false,
  isCorrect,
  count = 1,
}: LinesProps & {
  count?: 1 | 2 | 3 | 4 | 5;
}) => (
  <div className="relative flex items-end gap-6 justify-end w-full cursor-pointer">
    {Array.from({ length: count }).map((_, index) => (
      <Line key={index} className={className} />
    ))}

    <RoundButton className="text-4xl" size="sm" onClick={onClick}>
      {count}
    </RoundButton>

    {selected && isCorrect && (
      <Celebrate className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" />
    )}
  </div>
);

interface SegmentationPlayerProps {
  segmentation: string;
  onEnded?: () => void;
}

const SegmentationPlayer = ({
  segmentation,
  onEnded,
}: SegmentationPlayerProps) => {
  const playerRef = useRef<AudioPlayer>(null);

  const { sendAlert, getCdnUrls } = useUi();
  const [playingSegment, setPlayingSegment] = useState<number>(0);
  const [ended, setEnded] = useState(false);
  const sounds = useMemo(() => segmentation.split('-'), [segmentation]);
  const mediaUrls = useMemo(
    () => getCdnUrls(sounds, SymbolType.Sound, CdnObjectType.Audio),
    [sounds, getCdnUrls],
  );
  const [playerReady, setPlayerReady] = useState(false);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (!playerReady || started) {
      return;
    }

    setTimeout(() => {
      setStarted(true);
      playerRef.current?.play();
    }, 1000);
  }, [started, playerReady]);

  useEffect(() => {
    if (playingSegment === undefined) {
      return;
    }

    if (playingSegment === sounds.length - 1) {
      setTimeout(() => {
        onEnded?.();
        setEnded(true);
      }, 2000);
    }
  }, [playingSegment, sounds, onEnded]);

  return (
    <div className="flex flex-col gap-12 justify-center items-center">
      {playingSegment === 0 && <Lines count={1} />}

      {playingSegment === 1 && <Lines count={2} />}

      {playingSegment === 2 && <Lines count={3} />}

      {playingSegment === 3 && <Lines count={4} />}

      {playingSegment === 4 && <Lines count={5} />}

      {ended && (
        <div className="text-6xl font-sassoonInfant">{segmentation}</div>
      )}

      <AudioPlayer
        ref={playerRef}
        src={mediaUrls || ''}
        pause={200}
        onPlaySrc={(src, index) => {
          setPlayingSegment(index);
        }}
        onReadySrc={() => setPlayerReady(true)}
        onLoadTimeoutSrc={() =>
          sendAlert('We could not load some of the audio for this lesson.', {
            type: 'warning',
            title: 'There was a problem',
          })
        }
      />
    </div>
  );
};

interface CountGameProps {
  segmentation: string;
  selected?: number;
  setSelected: (segment: number | undefined) => void;
}

export const CountGame = ({
  segmentation,
  selected,
  setSelected,
}: CountGameProps) => {
  const playerRef = useRef<AudioPlayer>(null);
  const { sendAlert } = useUi();
  const { scrollNextSlide } = useLessonPlayer();
  const [playedOnce, setPlayedOnce] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);
  const [errorMode, setErrorMode] = useState(false);

  const word = useMemo(() => segmentation.replaceAll('-', ''), [segmentation]);
  const segmentCount = useMemo(
    () => segmentation.split('-').length,
    [segmentation],
  );

  const mediaUrl = useAssetCdn(word, SymbolType.Word, CdnObjectType.Audio);

  useEffect(() => {
    if (selected === undefined) {
      return;
    }

    setTimeout(() => {
      if (selected === segmentCount) {
        scrollNextSlide?.();
      } else {
        setErrorMode(true);
      }
    }, 2000);
  }, [selected, segmentCount, scrollNextSlide]);

  return (
    <>
      <Toolbar
        onPlay={() => {
          playerRef.current?.play();
          setPlayedOnce(true);
        }}
        hintPlay={!playedOnce}
        playDisabled={!playerReady}
        onRefresh={() => {
          setPlayedOnce(false);
        }}
      />

      <WhitePanel mode="basic" kind="fill" color="paper">
        <div className="md:flex w-full">
          <div className="flex-1">
            <CdnMedia
              className="block h-auto w-[320px] mx-auto"
              symbol={word}
              symbolType={SymbolType.Word}
              objectType={CdnObjectType.Image}
              width={200}
              height={200}
            />
          </div>

          <div className="w-full md:w-2/3">
            {errorMode ? (
              <SegmentationPlayer segmentation={segmentation} />
            ) : (
              <div className="flex flex-col gap-12">
                <Lines
                  count={1}
                  selected={selected === 1}
                  onClick={() => setSelected(1)}
                  isCorrect={segmentCount === 1}
                />

                <Lines
                  count={2}
                  selected={selected === 2}
                  onClick={() => setSelected(2)}
                  isCorrect={segmentCount === 2}
                />

                <Lines
                  count={3}
                  selected={selected === 3}
                  onClick={() => setSelected(3)}
                  isCorrect={segmentCount === 3}
                />

                <Lines
                  count={4}
                  selected={selected === 4}
                  onClick={() => setSelected(4)}
                  isCorrect={segmentCount === 4}
                />
              </div>
            )}
          </div>

          <AudioPlayer
            ref={playerRef}
            src={mediaUrl || ''}
            onReadySrc={() => setPlayerReady(true)}
            onLoadTimeoutSrc={() =>
              sendAlert(
                'We could not load some of the audio for this lesson.',
                {
                  type: 'warning',
                  title: 'There was a problem',
                },
              )
            }
          />
        </div>
      </WhitePanel>
    </>
  );
};
